/**
 * Polls the org-wide OncoEMR tasks inbox for unread (`is_updated`) notifications.
 * @module useOncoemrTasksInboxPoll
 */

import { useCallback, useEffect } from "react";
import { fetchOncoemrTasks } from "../../api/getCall/fetchOncoemrTasks";
import type { OncoemrTaskListItem } from "../../data-model/oncoemrTasksModel";
import { useAbortableFetch } from "../../hooks/useAbortableFetch";

/** Poll interval for the notification inbox while the tab is visible. */
export const INBOX_POLL_INTERVAL_MS = 30_000;

/** Parameters for {@link useOncoemrTasksInboxPoll}. */
export interface UseOncoemrTasksInboxPollParams {
  orgId: string;
  /** Authenticated user id; required for non-admin assignee-scoped polls. */
  userId: string;
  /**
   * When true, polls org-wide tasks without `user_id`.
   * When false, limits results to tasks assigned to {@link userId}.
   */
  isAdmin: boolean;
  /** When false, polling is stopped and in-flight requests are aborted. */
  enabled: boolean;
}

/**
 * Periodically fetches `GET /oncoemr-tasks` for the notification inbox.
 *
 * Admins receive org-wide unread tasks; non-admins only see tasks assigned to them.
 *
 * @param params - Org/user scope, admin flag, and whether polling is active.
 * @returns Latest task rows, loading/error state, and a manual refetch helper.
 */
export const useOncoemrTasksInboxPoll = ({
  orgId,
  userId,
  isAdmin,
  enabled,
}: UseOncoemrTasksInboxPollParams) => {
  const fetchInbox = useCallback(
    async (signal: AbortSignal) =>
      fetchOncoemrTasks(
        {
          org_id: orgId,
          ...(!isAdmin && userId ? { user_id: userId } : {}),
          limit: 100,
        },
        signal,
      ),
    [orgId, userId, isAdmin],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<OncoemrTaskListItem[]>(fetchInbox);

  const pollOnce = useCallback(async () => {
    if (!enabled || !orgId || !userId) {
      return;
    }
    await runFetch();
  }, [enabled, orgId, userId, runFetch]);

  useEffect(() => {
    if (!enabled || !orgId || !userId) {
      abort();
      return undefined;
    }

    void pollOnce();
    return () => abort();
  }, [enabled, orgId, userId, pollOnce, abort]);

  useEffect(() => {
    if (!enabled || !orgId || !userId) {
      return undefined;
    }

    const tick = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      void pollOnce();
    };

    const intervalId = window.setInterval(tick, INBOX_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [enabled, orgId, userId, pollOnce]);

  return {
    tasks: data ?? [],
    loading,
    error,
    refetch: pollOnce,
  };
};
